import React, { useState } from 'react';
import {
  Box, Typography, Switch, FormGroup, FormControlLabel, TextField, Button, Paper, Alert,
  InputAdornment, IconButton, Divider, Tooltip, Grid
} from '@mui/material';
import { useMaintenance } from '../../contexts/MaintenanceContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const MantenimientoPage: React.FC = () => {
  const {
    maintenanceBO,
    maintenanceClient,
    maintenanceConsumer,
    pinBO,
    pinClient,
    pinConsumer,
    messageBO,
    messageClient,
    messageConsumer,
    setMaintenance,
    updatePin,
    updateMessage
  } = useMaintenance();

  const [showSuccess, setShowSuccess] = useState(false);
  const [showPins, setShowPins] = useState({
    bo: false,
    client: false,
    consumer: false
  });
  const [tempPins, setTempPins] = useState({
    bo: pinBO,
    client: pinClient,
    consumer: pinConsumer
  });
  
  const [tempMessages, setTempMessages] = useState({
    bo: { ...messageBO },
    client: { ...messageClient },
    consumer: { ...messageConsumer }
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleTogglePinVisibility = (platform: 'bo' | 'client' | 'consumer') => {
    setShowPins(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const handlePinChange = (platform: 'bo' | 'client' | 'consumer', value: string) => {
    // Solo permitir números y máximo 4 dígitos
    if (/^\d{0,4}$/.test(value)) {
      setTempPins(prev => ({
        ...prev,
        [platform]: value
      }));
      
      // Limpiar error si existe
      if (errors[`${platform}PIN`]) {
        const newErrors = { ...errors };
        delete newErrors[`${platform}PIN`];
        setErrors(newErrors);
      }
    }
  };

  const handleSavePin = async (platform: 'bo' | 'client' | 'consumer') => {
    try {
      if (tempPins[platform].length !== 4) {
        setErrors(prev => ({
          ...prev,
          [`${platform}PIN`]: 'El PIN debe tener exactamente 4 dígitos'
        }));
        return;
      }
      
      await updatePin(platform, tempPins[platform]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error al actualizar el PIN:', error);
      setErrors(prev => ({
        ...prev,
        [`${platform}PIN`]: 'Error al guardar el PIN. Intente nuevamente.'
      }));
    }
  };
  
  const handleMessageChange = (platform: 'bo' | 'client' | 'consumer', field: 'title' | 'description', value: string) => {
    setTempMessages(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
    
    // Limpiar errores
    const errorKey = `${platform}Message${field.charAt(0).toUpperCase() + field.slice(1)}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };
  
  const handleSaveMessage = async (platform: 'bo' | 'client' | 'consumer') => {
    try {
      const message = tempMessages[platform];
      
      if (message.title.length > 100) {
        setErrors(prev => ({
          ...prev,
          [`${platform}MessageTitle`]: 'El título no puede tener más de 100 caracteres'
        }));
        return;
      }
      
      if (message.description.length > 300) {
        setErrors(prev => ({
          ...prev,
          [`${platform}MessageDescription`]: 'La descripción no puede tener más de 300 caracteres'
        }));
        return;
      }
      
      await updateMessage(platform, message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error al actualizar el mensaje:', error);
      setErrors(prev => ({
        ...prev,
        [`${platform}Message`]: 'Error al guardar el mensaje. Intente nuevamente.'
      }));
    }
  };

  const handleToggleMaintenance = async (platform: 'bo' | 'client' | 'consumer', currentState: boolean) => {
    try {
      await setMaintenance(platform, !currentState);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error al actualizar el estado de mantenimiento:', error);
    }
  };

  const renderPlatformCard = (title: string, platform: 'bo' | 'client' | 'consumer', isActive: boolean, currentPin: string) => {
    const currentMessage = platform === 'bo' ? messageBO : platform === 'client' ? messageClient : messageConsumer;
    
    return (
      <Paper
        elevation={3}
        sx={{
          p: 2, // Reducido el padding
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '2px solid',
          borderColor: 'error.main',
          borderRadius: 2,
          minWidth: 200, // Reducido a 200px
          flex: '0 0 auto',
          width: '30%' // Ancho relativo al contenedor
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {title}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={() => handleToggleMaintenance(platform, isActive)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color={isActive ? 'error' : 'textSecondary'}>
                  {isActive ? 'ACTIVADO' : 'DESACTIVADO'}
                </Typography>
              }
              labelPlacement="start"
            />
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Configuración de PIN
        </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          type={showPins[platform] ? 'text' : 'password'}
          variant="outlined"
          size="small"
          value={tempPins[platform]}
          onChange={(e) => handlePinChange(platform, e.target.value)}
          error={!!errors[`${platform}PIN`]}
          helperText={errors[`${platform}PIN`] || ' '}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePinVisibility(platform)}
                  edge="end"
                >
                  {showPins[platform] ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={() => handleSavePin(platform)}
          disabled={tempPins[platform] === currentPin}
          sx={{ height: '40px' }}
        >
          Guardar
        </Button>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2" gutterBottom>
        Mensaje de Mantenimiento
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Título"
          variant="outlined"
          size="small"
          value={tempMessages[platform].title}
          onChange={(e) => handleMessageChange(platform, 'title', e.target.value)}
          error={!!errors[`${platform}MessageTitle`]}
          helperText={errors[`${platform}MessageTitle`] || `${tempMessages[platform].title.length}/100`}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 1 }}
        />
        
        <TextField
          fullWidth
          label="Descripción"
          variant="outlined"
          size="small"
          multiline
          rows={3}
          value={tempMessages[platform].description}
          onChange={(e) => handleMessageChange(platform, 'description', e.target.value)}
          error={!!errors[`${platform}MessageDescription`]}
          helperText={errors[`${platform}MessageDescription`] || `${tempMessages[platform].description.length}/300`}
          inputProps={{ maxLength: 300 }}
          sx={{ mb: 1 }}
        />
        
        <Button
          variant="outlined"
          onClick={() => handleSaveMessage(platform)}
          disabled={
            tempMessages[platform].title === (platform === 'bo' ? messageBO : platform === 'client' ? messageClient : messageConsumer).title &&
            tempMessages[platform].description === (platform === 'bo' ? messageBO : platform === 'client' ? messageClient : messageConsumer).description
          }
          fullWidth
        >
          Guardar Mensaje
        </Button>
      </Box>
    </Paper>
  );
};

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Configuración de Mantenimiento
      </Typography>
      
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3, maxWidth: 600 }}>
          Configuración guardada correctamente.
        </Alert>
      )}

      <Typography variant="body1" paragraph>
        Configura el modo mantenimiento para cada plataforma. Cuando el modo mantenimiento está activado, los usuarios deberán ingresar un PIN de acceso.
      </Typography>

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 2, overflowX: 'auto', py: 2, px: 1, justifyContent: 'space-between', width: '100%' }}>
          {renderPlatformCard('Back Office', 'bo', maintenanceBO, pinBO)}
          {renderPlatformCard('Clientes', 'client', maintenanceClient, pinClient)}
          {renderPlatformCard('Consumidores', 'consumer', maintenanceConsumer, pinConsumer)}
        </Box>
      </Box>
    </Box>
  );
};

export default MantenimientoPage;
